import type { ButtonHTMLAttributes, ReactNode } from "react";
import { badgeFor, type ForumPerson } from "../api/forumApi";

/* The house palette, kept here with the components that use it rather than
   in the Tailwind config — nothing outside this module reads in it.

   NAVY is the portal's own blue, the one the graduate-pledge screens use on
   their buttons. Gold is an ACCENT and nothing else: the tab underline, the
   rule over a sidebar panel, the pinned badge, the mark down an unread row.
   Every button that does something is blue. */
export const NAVY = "#0b2e6b";
export const GOLD = "#D9A441";
export const CREAM = "#FDF8F0";
export const LINE = "#E6E8EC";

/* ── Buttons ─────────────────────────────────────────────────────────────
   Two of them, and only two: the blue one that does the thing, and the
   bordered one that backs out of it. They are components rather than a
   class string each page copies, because that is how the styling drifted
   in the first place — one screen's "post" ended up a different size,
   radius and text colour from the next one's. A new button in this module
   uses these or has a reason not to.

   `size="sm"` is the same button inside a card or a composer, where a
   full-height one would shout; nothing else about it changes. */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: "base" | "sm";
  children: ReactNode;
};

const SHAPE = "inline-flex items-center justify-center gap-2 rounded font-black transition-colors disabled:opacity-60";
const SIZES = {
  base: "px-5 py-3 text-[13px]",
  sm: "px-4 py-2 text-[12px]",
};

export function PrimaryButton({
  size = "base",
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={`${SHAPE} ${SIZES[size]} text-white hover:brightness-110 ${className}`}
      style={{ backgroundColor: NAVY, ...rest.style }}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  size = "base",
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={`${SHAPE} ${SIZES[size]} border bg-white hover:bg-gray-50 ${className}`}
      style={{ borderColor: LINE, color: NAVY, ...rest.style }}
    >
      {children}
    </button>
  );
}

/** A square bordered button holding one icon — refresh, pin, lock, edit. */
export function IconButton({
  active = false,
  className = "",
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      {...rest}
      className={`rounded border p-2.5 transition-colors hover:bg-gray-50 disabled:opacity-60 ${className}`}
      style={{
        borderColor: active ? "#E2CD9C" : LINE,
        backgroundColor: active ? CREAM : "white",
        color: active ? "#8A6516" : "#9AA3AE",
        ...rest.style,
      }}
    >
      {children}
    </button>
  );
}

/**
 * "5h ago" — a discussion reads by recency until it is old enough to have
 * a date instead.
 */
export function ago(iso: string | null | undefined) {
  if (!iso) return "—";
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);

  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;

  // Past a month, the date itself — with the year only when it is not this
  // one, because "6 Sept" reads better than "6 Sept 2026" on a page where
  // nearly everything is from this year.
  return then.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(then.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }),
  });
}

/** The full stamp, for a tooltip where the relative one is too vague. */
export function when(iso: string | null | undefined) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function initials(name?: string | null) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

/**
 * A stable colour per person.
 *
 * Derived from the name, so the same member is the same colour on every
 * screen and in every session. That consistency is the only thing that
 * makes a lettered circle worth anything as a way of recognising who is
 * speaking before you have read the name.
 */
const AVATAR_COLOURS = [NAVY, "#A5762A", "#3C5A77", "#6B6356", "#2F5D50"];

export function avatarColour(name?: string | null) {
  if (!name) return AVATAR_COLOURS[0];
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i);
  return AVATAR_COLOURS[sum % AVATAR_COLOURS.length];
}

export function Avatar({
  person,
  size = 44,
}: {
  person?: ForumPerson | null;
  size?: number;
}) {
  return (
    <div
      title={person?.name ?? undefined}
      className="flex shrink-0 items-center justify-center rounded-full font-black tracking-wide text-white"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size / 3.6),
        backgroundColor: avatarColour(person?.name),
      }}
    >
      {initials(person?.name)}
    </div>
  );
}

/** Name, badge, chapter and when — the line under every avatar. */
export function Byline({
  person,
  at,
  size = "base",
}: {
  person?: ForumPerson | null;
  at: string;
  size?: "base" | "sm";
}) {
  const badge = badgeFor(person);
  const text = size === "sm" ? "text-[13px]" : "text-[15px]";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`font-black ${text}`} style={{ color: NAVY }}>
        {person?.name ?? "Unknown"}
      </span>
      {badge && (
        <span
          className="rounded border px-2 py-0.5 text-[11px] font-black uppercase tracking-[0.06em]"
          style={{ borderColor: "#C9CFD8", color: NAVY }}
        >
          {badge}
        </span>
      )}
      <span className="text-[13px] text-gray-500" title={when(at)}>
        {person?.chapter || "National"} · {ago(at)}
      </span>
    </div>
  );
}
